import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Label} from '@/components/ui/label/Label'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {DateRangeFilterEnum} from '@/types'
import {useTranslation} from '@/translations/i18n'
import {ActivityReportCounters, ActivityReportSector} from '@/features/accesses/activity-report/types'
import {activityReportSectorToLabel} from '@/features/accesses/activity-report/utils'
import {ActivityReportCountersPanel} from './ActivityReportCountersPanel'
import {ActivityReportValidationBanner} from './ActivityReportValidationBanner'
import {ActivityReportExistingBanner} from './ActivityReportExistingBanner'
import {
    StyledActivityReportDialogBody,
    StyledActivityReportDialogFooter,
    StyledActivityReportEmptyHint,
    StyledActivityReportErrorBox,
    StyledActivityReportFieldRow
} from './style'

export type ActivityReportPeriod = {
    startDate: string
    endDate: string
    range: DateRangeFilterEnum
}

type ActivityReportConfigStepProps = {
    period: ActivityReportPeriod | null
    sector: ActivityReportSector | null
    onPeriodApply: (period: ActivityReportPeriod) => void
    onSectorChange: (sector: ActivityReportSector) => void
    counters: ActivityReportCounters | undefined
    isCountersLoading: boolean
    isCountersError: boolean
    isGenerating: boolean
    onGenerate: () => void
}

export const ActivityReportConfigStep = ({
    period,
    sector,
    onPeriodApply,
    onSectorChange,
    counters,
    isCountersLoading,
    isCountersError,
    isGenerating,
    onGenerate
}: ActivityReportConfigStepProps) => {
    const {t} = useTranslation()

    const sectorSteps = ActivityReportSector.options.map(value => ({
        label: t(activityReportSectorToLabel[value]),
        value
    }))

    const bothSelected = !!period && !!sector
    const accessesToValidate = counters ? Math.max(0, counters.totalAccesses - counters.validatedAccesses) : 0
    const absencesToValidate = counters ? Math.max(0, counters.totalAbsences - counters.validatedAbsences) : 0
    const hasItemsToValidate = accessesToValidate > 0 || absencesToValidate > 0
    const hasBillable = !!counters && counters.validatedAccesses + counters.validatedAbsences > 0
    // A billed existing report for this period+sector cannot be replaced, so generation is blocked.
    const existingReportBilled = !!counters?.existingReport?.isBilled
    // CTA enabled only when a month + sector are chosen, everything is validated, there is
    // something billable (FR-011 / FR-013), and no billed report already exists for the period.
    const canGenerate =
        bothSelected && !!counters && !isCountersLoading && !hasItemsToValidate && hasBillable && !existingReportBilled

    return (
        <>
            <StyledActivityReportDialogBody>
                <StyledActivityReportFieldRow>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{t('activity_report:field_period_label')}</Label>
                        <DateRangeFilter
                            values={period ?? undefined}
                            onApply={onPeriodApply}
                            visibleOptions={[DateRangeFilterEnum.Enum.month]}
                            placeholder={t('activity_report:select_period')}
                            portal
                            fullWidth
                        />
                    </Flexbox>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{t('activity_report:field_sector_label')}</Label>
                        <RadioSteps<ActivityReportSector>
                            fullWidth
                            itemWidth="100%"
                            size="md"
                            name="activity-report-sector"
                            steps={sectorSteps}
                            value={sector ?? undefined}
                            onChange={onSectorChange}
                        />
                    </Flexbox>
                </StyledActivityReportFieldRow>

                {!bothSelected && (
                    <StyledActivityReportEmptyHint>{t('activity_report:empty_hint')}</StyledActivityReportEmptyHint>
                )}

                {bothSelected && isCountersError && (
                    <StyledActivityReportErrorBox>
                        <AlertTriangleIcon size={16} />
                        {t('activity_report:counters_error')}
                    </StyledActivityReportErrorBox>
                )}

                {bothSelected && !isCountersError && !isCountersLoading && hasItemsToValidate && (
                    <ActivityReportValidationBanner
                        accessesToValidate={accessesToValidate}
                        absencesToValidate={absencesToValidate}
                    />
                )}
                {bothSelected && !isCountersError && !isCountersLoading && counters?.existingReport && (
                    <ActivityReportExistingBanner
                        createdAt={counters.existingReport.createdAt}
                        isBilled={counters.existingReport.isBilled}
                    />
                )}
                {bothSelected && !isCountersError && (
                    <ActivityReportCountersPanel counters={counters} isLoading={isCountersLoading} />
                )}
            </StyledActivityReportDialogBody>
            <StyledActivityReportDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <Button variant="primaryNeutral" onClick={onGenerate} disabled={!canGenerate || isGenerating}>
                    {t('activity_report:generate_cta')}
                    {isGenerating && <Spinner />}
                </Button>
            </StyledActivityReportDialogFooter>
        </>
    )
}
